"use client"

import { useState, useEffect, useMemo } from "react"
import { Card, CardHeader, CardBody, Badge, Spinner, Alert, Button, Input } from "reactstrap"
import DataTable from "react-data-table-component" // Import DataTable
import { BadgeService } from "../../../Service/myBadgeOrderService" // Adjust path based on your project structure
import type { PrintOrder } from "./types" // Adjust path
import { toast } from "react-toastify"
import { FaClipboardList, FaCalendarAlt, FaTag, FaUser, FaBuilding, FaEye } from "react-icons/fa"
import OrderDetailsModal from "./OrderDetailsModal" // Import the new modal component

interface MyBadgeOrdersListProps {
  refreshTrigger: boolean // Prop to trigger re-fetching data
}

/**
 * Displays a list of badge orders made by the current authenticated user using DataTable.
 */
export default function MyBadgeOrdersList({ refreshTrigger }: MyBadgeOrdersListProps) {
  const [orders, setOrders] = useState<PrintOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState("") // State for search input

  // State for the order details modal
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<PrintOrder | null>(null)

  const fetchMyOrders = async () => {
    setLoading(true)
    setError(null) // Clear previous errors
    try {
      const fetchedOrders = await BadgeService.getMyBadgeOrders()
      setOrders(fetchedOrders)
    } catch (err: any) {
      console.error("Failed to fetch my badge orders:", err)
      setError(err.response?.data?.detail || "Failed to load your badge orders.")
      toast.error("Failed to load your badge orders.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMyOrders()
  }, [refreshTrigger]) // Re-fetch data when refreshTrigger changes

  // Helper function to determine badge color based on status
  const getStatusBadgeColor = (status: PrintOrder["status"]) => {
    switch (status) {
      case "pending":
        return "warning"
      case "paid":
        return "info"
      case "approved":
        return "success"
      case "rejected":
        return "danger"
      case "printed":
        return "primary"
      default:
        return "secondary"
    }
  }

  // Function to open the details modal
  const handleViewDetails = (order: PrintOrder) => {
    setSelectedOrder(order)
    setIsDetailsModalOpen(true)
  }

  // Function to close the details modal
  const toggleDetailsModal = () => {
    setIsDetailsModalOpen(!isDetailsModalOpen)
    if (isDetailsModalOpen) {
      setSelectedOrder(null) // Clear selected order when closing
    }
  }

  // Filter data based on search input
  const filteredOrders = useMemo(() => {
    const searchTerm = search.toLowerCase()
    return orders.filter((order) => {
      const orderNumber = order.order_number?.toLowerCase() || ""
      const status = order.status?.toLowerCase() || ""
      const representativeName =
        order.order_details?.[0]?.representation_details?.person_details?.full_name?.toLowerCase() || ""
      const exhibitorName =
        order.order_details?.[0]?.representation_details?.exhibitor_details?.name?.toLowerCase() || ""
      const exhibitionTitle = order.participation_details?.exhibition_title?.toLowerCase() || ""

      return (
        orderNumber.includes(searchTerm) ||
        status.includes(searchTerm) ||
        representativeName.includes(searchTerm) ||
        exhibitorName.includes(searchTerm) ||
        exhibitionTitle.includes(searchTerm)
      )
    })
  }, [orders, search])

  // Define columns for DataTable
  const columns = useMemo(
    () => [
      {
        name: "Order Number",
        selector: (row: PrintOrder) => row.order_number,
        sortable: true,
        cell: (row: PrintOrder) => (
          <div>
            <FaTag className="me-1 text-muted" size={12} />
            {row.order_number}
          </div>
        ),
        minWidth: "150px",
      },
      {
        name: "Status",
        selector: (row: PrintOrder) => row.status,
        sortable: true,
        cell: (row: PrintOrder) => (
          <Badge color={getStatusBadgeColor(row.status)} pill>
            {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
          </Badge>
        ),
        minWidth: "100px",
      },
      {
        name: "Requested On",
        selector: (row: PrintOrder) => new Date(row.created_at).toLocaleDateString(),
        sortable: true,
        cell: (row: PrintOrder) => (
          <div>
            <FaCalendarAlt className="me-1 text-muted" size={12} />
            {new Date(row.created_at).toLocaleDateString()}
          </div>
        ),
        minWidth: "140px",
      },
      
      {
        name: "Exhibition",
        selector: (row: PrintOrder) => row.participation_details?.exhibition_title || "N/A",
        sortable: true,
        minWidth: "150px",
      },
      {
        name: "Actions",
        cell: (row: PrintOrder) => (
          <Button color="info" size="xs" onClick={() => handleViewDetails(row)}>
            <FaEye className="me-1" /> 
          </Button>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: "100px",
      },
    ],
    [], // Dependencies for useMemo, empty as columns are static
  )

  return (
    <Card className="shadow-sm">
      <CardHeader className="bg-light d-flex justify-content-between align-items-center">
        <h5 className="mb-0 text-primary">
          <FaClipboardList className="me-2" /> My Badge Orders
        </h5>
      </CardHeader>
      <CardBody>
        <div className="d-flex justify-content-end mb-3">
          <Input
            type="search"
            placeholder="Search orders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 300 }}
          />
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner color="primary" />
            <span className="ms-2">Loading orders...</span>
          </div>
        ) : error ? (
          <Alert color="danger" className="text-center">
            {error}
          </Alert>
        ) : (
          <DataTable
            columns={columns}
            data={filteredOrders}
            progressPending={loading}
            pagination
            highlightOnHover
            striped
            noDataComponent="No badge orders found."
            
          />
        )}
      </CardBody>
      {selectedOrder && (
        <OrderDetailsModal isOpen={isDetailsModalOpen} toggle={toggleDetailsModal} order={selectedOrder} />
      )}
    </Card>
  )
}
